// The player's armour: the one thing loop-scoped coin can be spent on that
// outlives the loop. Battle loot (LoopState.money) resets on death like
// everything else temporary, but a suit of armour bought with it is recorded in
// PermanentState and carried forward — so a lucky run of won battles can be
// banked into a lasting edge, which is the whole point of picking coin up.
//
// Each tier reduces the damage the player's own line takes in every battle
// (see battleEngine.getPlayerBattleModifiers), stacking with the Survival
// ability. Tiers are bought one at a time, cheapest first, and each must be
// paid for out of a *single* loop's earnings — money never carries across
// death, so a better suit is always a run's worth of fighting away.

import type { PermanentState } from "../types"

export interface ArmorTier {
  /** 1-based rank; also the value stored in PermanentState.armorTier once owned. */
  tier: number
  name: string
  /** Coin (LoopState.money) needed to buy this tier. */
  cost: number
  /** Fraction of incoming battle damage this suit turns aside, on top of Survival. */
  incomingReduction: number
  blurb: string
}

/**
 * The suits, cheapest first. Prices are set against battle loot (roughly
 * 25–220 coin a fight, plus objective bonuses): the leather is a first war's
 * reward, full plate the work of a determined campaign — and because coin dies
 * with the loop, each rung has to be earned inside one life.
 */
export const ARMOR_TIERS: ArmorTier[] = [
  {
    tier: 1,
    name: "Reinforced Leather",
    cost: 150,
    incomingReduction: 0.06,
    blurb: "Boiled hide and iron studding. It will not stop a giant, but it turns the glancing blows that kill tired soldiers.",
  },
  {
    tier: 2,
    name: "Chainmail Hauberk",
    cost: 350,
    incomingReduction: 0.11,
    blurb: "A full shirt of riveted mail. The difference between a wound and a scar across a long war.",
  },
  {
    tier: 3,
    name: "Dwarven Plate",
    cost: 700,
    incomingReduction: 0.17,
    blurb: "Fitted plate off a Stonehall anvil, proof against nearly anything that is not aimed with malice and a warhammer.",
  },
]

/** The tier the player currently owns (0 = none). Tolerant of pre-armour saves. */
export function getArmorTier(permanent: PermanentState): number {
  return permanent.armorTier ?? 0
}

/** The incoming-damage reduction the owned suit grants, or 0 with no armour. */
export function getArmorReduction(permanent: PermanentState): number {
  const tier = getArmorTier(permanent)
  return tier > 0 ? (ARMOR_TIERS[tier - 1]?.incomingReduction ?? 0) : 0
}

/** The next suit up the player could buy, or null once they wear the best. */
export function getNextArmorTier(permanent: PermanentState): ArmorTier | null {
  return ARMOR_TIERS[getArmorTier(permanent)] ?? null
}

/** The suit the player is wearing now, or null if none. */
export function getCurrentArmorTier(permanent: PermanentState): ArmorTier | null {
  const tier = getArmorTier(permanent)
  return tier > 0 ? (ARMOR_TIERS[tier - 1] ?? null) : null
}
